import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Loader2, PenLine, Send, Ban, AlertTriangle, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

const STATO = {
  in_attesa: ["In attesa di firma", "bg-amber-500/15 text-amber-400 border-amber-500/40"],
  firmata: ["Firmato", "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"],
  firmata_parziale: ["Ha firmato · attende l'altro", "bg-sky-500/15 text-sky-400 border-sky-500/40"],
  scaduta: ["Scaduto", "bg-slate-500/15 text-slate-400 border-slate-500/40"],
  revocata: ["Revocato", "bg-red-500/15 text-red-400 border-red-500/40"],
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "");
const LBL = "text-[11px] uppercase tracking-wider text-slate-400 font-mono";

const Richiesta = ({ r, onRevoke }) => {
  const [label, cls] = STATO[r.stato] || STATO.in_attesa;
  return (
    <div className="rounded-md border border-slate-700 p-2.5 text-xs space-y-1" data-testid={`firma-req-${r.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-200 font-medium truncate">{r.gruppo_id ? `${r.slot + 1}° firmatario · ` : ""}{r.firmatario ? `${r.firmatario} · ` : ""}{r.email}</span>
        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${cls}`} data-testid={`firma-req-stato-${r.id}`}>{label}</span>
      </div>
      <div className="text-slate-400">Inviato {fmt(r.created_at)}{r.signed_at ? ` · Firmato ${fmt(r.signed_at)}` : ` · Scade ${fmt(r.expires_at)}`}</div>
      {r.stato === "firmata" && <div className="text-emerald-400">{r.gruppo_id ? "Firmato da entrambi: documento " : "Documento "}firmato salvato negli allegati della pratica</div>}
      {r.stato === "in_attesa" && (
        <button type="button" onClick={() => onRevoke(r)} data-testid={`firma-req-revoca-${r.id}`} className="inline-flex items-center gap-1 text-red-400 hover:text-red-300"><Ban className="h-3 w-3" /> Revoca link{r.gruppo_id ? " (per entrambi)" : ""}</button>
      )}
    </div>
  );
};

export const FirmaRemotaDialog = ({ open, onOpenChange, pratica, lettera, defaultEmail, defaultName, buildPdf }) => {
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [due, setDue] = useState(false);
  const [privacy, setPrivacy] = useState(true);
  const [email2, setEmail2] = useState("");
  const [nome2, setNome2] = useState("");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const base = `/studio/pratiche/${pratica?.id}/firme-remote`;
  const load = () => api.get(base, { params: { lettera_id: lettera?.id } }).then(({ data }) => setList(data)).catch(() => {});

  useEffect(() => {
    if (open) { setEmail(defaultEmail || ""); setNome(defaultName || ""); setDue(false); setEmail2(""); setNome2(""); setPrivacy(true); load(); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const invia = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { toast.error("Inserisci un'email valida"); return; }
    if (due && (!nome2.trim() || !/^\S+@\S+\.\S+$/.test(email2.trim()))) { toast.error("Inserisci nome ed email validi del secondo firmatario"); return; }
    if (due && email2.trim().toLowerCase() === email.trim().toLowerCase()) { toast.error("I due firmatari devono avere email diverse"); return; }
    setBusy(true);
    try {
      const out = await buildPdf();
      if (!out?.b64) throw new Error("pdf");
      if (!out.marker) toast.warning("Dicitura \"Firma del Cliente\" non trovata: la firma verrà apposta su una pagina finale");
      await api.post(base, { lettera_id: lettera.id, documento: lettera.nome, email: email.trim(), firmatario: nome.trim(),
        ...(due ? { email2: email2.trim(), firmatario2: nome2.trim() } : {}), con_privacy: privacy,
        pdf_b64: out.b64, marker: out.marker, app_url: window.location.origin });
      toast.success(due ? `Link di firma inviati a ${email.trim()} e ${email2.trim()}` : `Link di firma inviato a ${email.trim()}`);
      load();
    } catch (e) { toast.error(e.message === "pdf" ? "Generazione PDF non riuscita" : formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revoca = async (r) => {
    if (!window.confirm(r.gruppo_id ? "Revocare i link di firma inviati a entrambi i firmatari?" : `Revocare il link inviato a ${r.email}?`)) return;
    try { await api.post(`${base}/${r.id}/revoca`); toast.success("Link revocato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg max-h-[88vh] overflow-y-auto" data-testid="firma-remota-dialog">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><PenLine className="h-4 w-4 text-amber-400" /> Firma a distanza</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">Il cliente riceve via email il link a <strong className="text-slate-200">{lettera?.nome}</strong>, verifica l'email con un codice OTP e accetta con un click. Il documento firmato, con data, ora e registro, viene salvato negli allegati della pratica. Link valido 7 giorni.</p>
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> La firma viene apposta sotto la dicitura "Firma del Cliente": lascia spazio libero sotto quella riga.
        </div>
        <div className="space-y-3">
          <div><span className={LBL}>Nome del firmatario</span><Input value={nome} onChange={(e) => setNome(e.target.value)} data-testid="firma-remota-nome" className="mt-1 h-9 bg-slate-900/60 border-slate-700" /></div>
          <div><span className={LBL}>Email del cliente</span><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="firma-remota-email" className="mt-1 h-9 bg-slate-900/60 border-slate-700" /></div>
          <label className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2.5 text-xs text-slate-300 cursor-pointer" data-testid="firma-remota-privacy-wrap">
            <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="firma-remota-privacy" className="mt-0.5 h-4 w-4 accent-emerald-600" />
            <span><span className="font-semibold text-emerald-300">Allega l'informativa privacy da firmare</span><br />Viene aggiunta in fondo al documento: con la firma il cliente la accetta e il consenso viene registrato nella sua scheda.</span>
          </label>
          {!due ? (
            <button type="button" onClick={() => setDue(true)} data-testid="firma-remota-add-secondo" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 border border-dashed border-amber-500/50 rounded-md px-2.5 py-1.5 hover:bg-amber-500/10">
              <UserPlus className="h-3.5 w-3.5" /> Aggiungi secondo firmatario
            </button>
          ) : (
            <div className="rounded-md border border-sky-500/30 bg-sky-500/5 p-2.5 space-y-2" data-testid="firma-remota-secondo">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-sky-300">Secondo firmatario (firma congiunta)</span>
                <button type="button" onClick={() => { setDue(false); setEmail2(""); setNome2(""); }} data-testid="firma-remota-remove-secondo" className="text-slate-400 hover:text-red-400" title="Rimuovi secondo firmatario"><X className="h-4 w-4" /></button>
              </div>
              <div><span className={LBL}>Nome del secondo firmatario</span><Input value={nome2} onChange={(e) => setNome2(e.target.value)} data-testid="firma-remota-nome2" className="mt-1 h-9 bg-slate-900/60 border-slate-700" /></div>
              <div><span className={LBL}>Email del secondo firmatario</span><Input type="email" value={email2} onChange={(e) => setEmail2(e.target.value)} data-testid="firma-remota-email2" className="mt-1 h-9 bg-slate-900/60 border-slate-700" /></div>
              <p className="text-[11px] text-slate-400">Entrambi ricevono il link e firmano in qualsiasi ordine. Il mandato è valido solo quando hanno firmato tutti e due; il secondo riquadro firma va sotto il primo.</p>
            </div>
          )}
          <Button onClick={invia} disabled={busy} data-testid="firma-remota-invia" className="w-full bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} {due ? "Invia link ai due firmatari" : "Invia link di firma"}
          </Button>
        </div>
        {list.length > 0 && (
          <div className="space-y-2" data-testid="firma-remota-list">
            <span className={LBL}>Richieste per questo documento</span>
            {list.map((r) => <Richiesta key={r.id} r={r} onRevoke={revoca} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
